import { Flex, Modal, Spin } from 'antd';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

const StyledModal = styled(Modal)`
.ant-modal-body {
  padding: 0;
  height: 75vh;
  }
`;

const StyledSpinnerContainer = styled(Flex)`
  height: 75vh;
`;

const StyledIFrame = styled.iframe`
  width: 100%;
  height: 75vh;
  border: none;
`;

interface DocPreviewModalProps {
  isOpen: boolean;
  url: string | null;
  fileName: string | null;
  isLoading: boolean;
  onClose: () => void;
}

const DocPreviewModal: FC<DocPreviewModalProps> = ({ isOpen, url, fileName, isLoading, onClose }) => {
  const content = url ? <StyledIFrame src={url} title={fileName ?? 'Document preview'} /> : null;

  return (
    <StyledModal open={isOpen} title={fileName} onCancel={onClose} footer={null} width="80vW" destroyOnHidden>
      {isLoading ? (
        <StyledSpinnerContainer align="center" justify="center">
          <Spin size="large" />
        </StyledSpinnerContainer>
      ) : (
        content
      )}
    </StyledModal>
  );
};

type PreviewState = {
  isOpen: boolean;
  url: string | null;
  fileName: string | null;
  isLoading: boolean;
};

const INITIAL_STATE: PreviewState = { isOpen: false, url: null, fileName: null, isLoading: false };

export const useDocPreviewModal = (download: ((driveFileId: string) => Promise<Blob>) | undefined) => {
  const [previewState, setPreviewState] = useState<PreviewState>(INITIAL_STATE);

  const handleOpenDocFile = useCallback(
    (driveFileId: string, fileName?: string) => async () => {
      if (!download) return;
      setPreviewState({ isOpen: true, url: null, fileName: fileName ?? null, isLoading: true });
      try {
        const blob = await download(driveFileId);
        const url = URL.createObjectURL(blob);
        setPreviewState(prev => ({ ...prev, url, isLoading: false }));
      } catch {
        setPreviewState(INITIAL_STATE);
      }
    },
    [download],
  );

  const closeModal = useCallback(() => {
    if (previewState.url) {
      URL.revokeObjectURL(previewState.url);
    }
    setPreviewState(INITIAL_STATE);
  }, [previewState.url]);

  return {
    isOpen: previewState.isOpen,
    url: previewState.url,
    fileName: previewState.fileName,
    isLoading: previewState.isLoading,
    handleOpenDocFile,
    closeModal,
    Modal: DocPreviewModal,
  };
};
